import React, { useMemo, useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { endpoints, externalApiCall } from '../api/config';
import { useCachedData } from '../hooks/useCachedData';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Search as SearchIcon, Refresh as RefreshIcon, ViewColumn as ViewColumnIcon, SaveAlt as SaveAltIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const flatten = (incoming) => {
  if (!incoming) return [];
  const rows = Array.isArray(incoming) ? incoming : (incoming?.data && Array.isArray(incoming.data) ? incoming.data : [incoming]);
  const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
  const deepFlatten = (obj, prefix = '') => {
    if (!isObject(obj)) return obj;
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      const key = prefix ? `${prefix}${k}` : k;
      if (isObject(v)) Object.assign(out, deepFlatten(v, `${key}_`));
      else out[key] = v;
    });
    return out;
  };
  return rows.map((r) => (isObject(r) ? deepFlatten(r) : { value: r }));
};

const FedexShippingAnalysis = () => {
  const [rawData, setRawData] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();
  const actionTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    textDecoration: 'none',
    textUnderlineOffset: '2px',
    '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
  };

  // API credentials
  const username = 'jy-fedex-admin';
  const password = '2wRN8Trg99j4kDkAuvpi';

  // Custom caching implementation similar to useCachedData
  const cacheKey = 'fedex-shipping-analysis';
  const [cacheData, setCacheData] = useState(() => {
    const cached = sessionStorage.getItem(cacheKey);
    const cacheTime = sessionStorage.getItem(`${cacheKey}-time`);
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
    
    if (cached && cacheTime && (now - parseInt(cacheTime)) < CACHE_DURATION) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(h);
  }, [searchTerm]);

  // Selection state for summary -> details
  const [showSummary, setShowSummary] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null); // { date, version }

  // Version summary (fetched live)
  const [summaryRows, setSummaryRows] = useState([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const fetchVersionSummary = async () => {
    setIsLoadingSummary(true);
    setSummaryError('');
    try {
      const credentials = btoa(`${username}:${password}`);
      const resp = await fetch('https://fedexstats-production.up.railway.app/version-summary', {
        method: 'GET',
        headers: { Authorization: `Basic ${credentials}`, Accept: 'application/json' }
      });
      if (!resp.ok) {
        const msg = await resp.text().catch(() => resp.statusText);
        throw new Error(msg || `HTTP ${resp.status}`);
      }
      const data = await resp.json().catch(() => null);

      const today = new Date().toISOString().split('T')[0];
      let rows = [];

      // Helper to normalize a single entry (primitive or object)
      const pushEntry = (entry, fallbackDate) => {
        if (entry === null || entry === undefined) return;
        if (typeof entry === 'number' || (typeof entry === 'string' && entry.trim() !== '')) {
          rows.push({ date: String(fallbackDate || today), version: String(entry) });
          return;
        }
        if (typeof entry === 'object') {
          const versionNum = (entry.version_number ?? entry.run_version ?? entry.version ?? entry.v ?? null);
          let runDate = entry.run_date ?? entry.date ?? fallbackDate ?? today;
          if (typeof runDate === 'string' && runDate.includes('T')) runDate = runDate.split('T')[0];
          if (versionNum !== null && versionNum !== undefined) rows.push({ date: String(runDate), version: String(versionNum) });
        }
      };

      // Primary: array input
      if (Array.isArray(data)) {
        data.forEach((e) => pushEntry(e, today));
      } else if (data && typeof data === 'object') {
        // Cases: { date, versions:[..] } where versions may be numbers OR objects
        if (Array.isArray(data.versions)) {
          const baseDate = data.date || today;
          data.versions.forEach((v) => pushEntry(v, baseDate));
        } else {
          // Cases: { 'YYYY-MM-DD': [ ..versions ] } where versions may be numbers OR objects
          Object.entries(data).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => pushEntry(v, key));
            }
          });
        }
      }
      // Deduplicate and sort by date desc first, then version desc
      const uniq = new Map();
      rows.forEach((r) => uniq.set(`${r.date}_${r.version}`, r));
      rows = Array.from(uniq.values()).sort((a, b) => {
        const byDate = String(b.date).localeCompare(String(a.date));
        if (byDate !== 0) return byDate;
        const av = parseInt(a.version, 10);
        const bv = parseInt(b.version, 10);
        if (!isNaN(av) && !isNaN(bv) && bv !== av) return bv - av;
        return 0;
      });
      setSummaryRows(rows);
    } catch (e) {
      setSummaryError(e.message || 'Failed to load version summary');
      setSummaryRows([]);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  useEffect(() => { fetchVersionSummary(); }, []);

  // State for detailed data fetched with version parameter
  const [detailedData, setDetailedData] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [downloadingIdx, setDownloadingIdx] = useState(null);
  const [detailsError, setDetailsError] = useState('');
  const [isRecalling, setIsRecalling] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch detailed data with version parameter
  const fetchDetailedData = async (version) => {
    setIsLoadingDetails(true);
    setDetailsError('');
    try {
      const url = `${endpoints.fedexShippingAnalysis}&version=${version}`;
      console.log('Fetching data with URL:', url);
      const data = await externalApiCall(url, username, password);
      console.log('API Response:', data);
      setDetailedData(data);
      
      // Try to cache the data with error handling
      try {
        const dataString = JSON.stringify(data);
        // Check if data is too large for sessionStorage (typically 5-10MB limit)
        if (dataString.length > 2 * 1024 * 1024) { // 2MB limit
          console.warn('Data too large for sessionStorage, skipping cache');
        } else {
          sessionStorage.setItem(cacheKey, dataString);
          sessionStorage.setItem(`${cacheKey}-time`, Date.now().toString());
          setCacheData(data);
        }
      } catch (storageError) {
        console.warn('Failed to cache data to sessionStorage:', storageError.message);
        // Clear old cache if quota exceeded
        if (storageError.name === 'QuotaExceededError') {
          try {
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(`${cacheKey}-time`);
            // Try again with smaller data
            const dataString = JSON.stringify(data);
            if (dataString.length <= 1024 * 1024) { // 1MB limit
              sessionStorage.setItem(cacheKey, dataString);
              sessionStorage.setItem(`${cacheKey}-time`, Date.now().toString());
              setCacheData(data);
            }
          } catch (retryError) {
            console.warn('Retry caching failed:', retryError.message);
          }
        }
      }
    } catch (err) {
      setDetailsError(err.message || 'Failed to fetch detailed data');
      console.error('Error fetching detailed data:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleDownloadVersionCsv = async (version, idx) => {
    try {
      setDownloadingIdx(idx);
      const today = new Date().toISOString().split('T')[0];
      const csvUrl = `${endpoints.fedexShippingAnalysis.replace('format=json', 'format=csv')}&version=${version}`;
      const credentials = btoa(`${username}:${password}`);
      const resp = await fetch(csvUrl, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${credentials}`,
          Accept: 'text/csv'
        }
      });
      if (!resp.ok) {
        const msg = await resp.text().catch(() => resp.statusText);
        throw new Error(msg || `HTTP ${resp.status}`);
      }
      const csvText = await resp.text();
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = `fedex-recommended-cost-${today}-v${version}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('CSV download failed:', e);
    } finally {
      setDownloadingIdx(null);
    }
  };

  const flatData = useMemo(() => flatten(detailedData), [detailedData]);

  // Initialize/update visible columns based on ALL data keys
  useEffect(() => {
    if (Array.isArray(flatData) && flatData.length > 0) {
      const keySet = new Set();
      flatData.forEach((row) => {
        if (row && typeof row === 'object') Object.keys(row).forEach((k) => keySet.add(k));
      });
      setVisibleColumns((prev) => {
        const next = { ...prev };
        Array.from(keySet).forEach((k) => { if (typeof next[k] === 'undefined') next[k] = true; });
        return next;
      });
    }
  }, [flatData]);

  const allKeys = useMemo(() => {
    if (!Array.isArray(flatData) || flatData.length === 0) return [];
    const keySet = new Set();
    flatData.forEach((row) => { if (row && typeof row === 'object') Object.keys(row).forEach((k) => keySet.add(k)); });
    return Array.from(keySet).sort((a, b) => a.localeCompare(b));
  }, [flatData]);
  const visibleKeys = useMemo(() => allKeys.filter((k) => visibleColumns[k] !== false), [allKeys, visibleColumns]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(flatData)) return [];
    const lower = debouncedSearch.toLowerCase();
    return flatData.filter((row) => lower ? allKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(lower)) : true);
  }, [flatData, debouncedSearch, allKeys]);

  const sortedRows = useMemo(() => [...filteredRows], [filteredRows]);

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try { 
      // Refresh the detailed data if we have a selected group
      if (selectedGroup) {
        await fetchDetailedData(selectedGroup.version);
      }
    } finally { 
      setIsRefreshing(false); 
    }
  };

  const handleColumnMenuOpen = (e) => setColumnMenuAnchorEl(e.currentTarget);
  const handleColumnMenuClose = () => setColumnMenuAnchorEl(null);
  const toggleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  // Trigger recalculation run (no data display, just fire-and-notify)
  const handleRecalClick = async () => {
    if (isRecalling) return;
    setIsRecalling(true);
    try {
      const credentials = btoa(`${username}:${password}`);
      const resp = await fetch('https://fedexstats-production.up.railway.app/run-shipping-analysis?format=json', {
        method: 'POST',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.message || `HTTP ${resp.status}`);
      }
      setSnackbar({ open: true, message: 'Recal completed successfully', severity: 'success' });
    } catch (e) {
      console.error('Recal failed', e);
      setSnackbar({ open: true, message: `Recal failed: ${e.message || e}`, severity: 'error' });
    } finally {
      setIsRecalling(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!sortedRows.length || visibleKeys.length === 0) return;
    const header = visibleKeys.join(',');
    const process = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
      return String(val).replace(/"/g, '""');
    };
    const rows = sortedRows.map((row) => visibleKeys.map((k) => `"${process(row[k])}"`).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'fedex_shipping_analysis.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const summaryGridColumns = useMemo(
    () => [
      { field: 'date', headerName: 'Date', resizable: true, flex: 1, minWidth: 150 },
      { field: 'version', headerName: 'Version', resizable: true, flex: 0.8, minWidth: 120 },
      {
        field: 'actions',
        headerName: 'Actions',
        resizable: false,
        sortable: false,
        filterable: false,
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const version = params.row?.version;
          const idx = params.row?.idx;
          const loadingThis = downloadingIdx === idx;
          return (
            <Box
              component="span"
              role="button"
              tabIndex={loadingThis ? -1 : 0}
              onClick={() => handleDownloadVersionCsv(version, idx)}
              onKeyDown={(e) => {
                if (loadingThis) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDownloadVersionCsv(version, idx);
                }
              }}
              sx={{
                ...actionTextSx,
                opacity: loadingThis ? 0.6 : 1,
                pointerEvents: loadingThis ? 'none' : 'auto',
              }}
            >
              Download
            </Box>
          );
        },
      },
    ],
    [downloadingIdx]
  );
  const summaryGridRows = useMemo(
    () => summaryRows.map((row, idx) => ({ id: `${row.date}_${row.version}_${idx}`, idx, ...row })),
    [summaryRows]
  );
  const detailGridColumns = useMemo(
    () =>
      visibleKeys.map((key) => ({
        field: key,
        headerName: key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        resizable: true,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const val = params.row?.[key];
          if (val === null || val === undefined || val === '') return '-';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        },
      })),
    [visibleKeys]
  );
  const detailGridRows = useMemo(
    () =>
      sortedRows.map((row, idx) => ({
        ...row,
        id: row.id ?? row._id ?? row.transaction_id ?? row.tracking_number ?? `detail-${idx}`,
      })),
    [sortedRows]
  );

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0, fontSize: '1.25rem' }}>
            FedEx Recommended Cost
          </Typography>
          <Box
            component="span"
            role="button"
            tabIndex={isRecalling ? -1 : 0}
            onClick={isRecalling ? undefined : handleRecalClick}
            onKeyDown={(e) => {
              if (isRecalling) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleRecalClick();
              }
            }}
            sx={{
              ...actionTextSx,
              color: isRecalling ? 'text.disabled' : 'primary.main',
              opacity: isRecalling ? 0.7 : 1,
              pointerEvents: isRecalling ? 'none' : 'auto',
            }}
          >
            {isRecalling ? <CircularProgress size={18} /> : 'Recal'}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
            <TextField
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{ 
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.875rem' }
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, gap: 0.5, alignItems: 'center' }}>
            {!showSummary && (
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setShowSummary(true);
                  setSelectedGroup(null);
                  setDetailedData(null);
                  setSearchTerm('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowSummary(true);
                    setSelectedGroup(null);
                    setDetailedData(null);
                    setSearchTerm('');
                  }
                }}
                sx={actionTextSx}
              >
                <ArrowBackIcon fontSize="small" />
                Back to Summary
              </Box>
            )}
            <Tooltip title="Refresh data">
              <span>
                <IconButton onClick={handleRefreshClick} disabled={isRefreshing || isLoading} size="small" aria-label="Refresh data" sx={{ padding: '6px' }}>
                  {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <GlobalDenseModeToolbar
              denseMode={denseMode}
              denseModeTogglePending={denseModeTogglePending}
              persistDenseMode={persistDenseMode}
            />
            <Tooltip title="Show/Hide Columns">
              <IconButton onClick={handleColumnMenuOpen} size="small" aria-label="Show/Hide Columns" sx={{ padding: '6px' }}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={columnMenuAnchorEl} open={Boolean(columnMenuAnchorEl)} onClose={handleColumnMenuClose} PaperProps={{ sx: { maxHeight: '70vh', minWidth: 220, p: 1 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show/Hide Columns</Typography>
                <IconButton size="small" onClick={handleColumnMenuClose} sx={{ p: 0.5 }} aria-label="Close column visibility menu">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              {allKeys.map((k) => (
                <MenuItem key={k} onClick={() => toggleColumn(k)} sx={{ fontSize: '0.875rem', minHeight: 'auto', py: 0.75, px: 1 }}>
                  <Checkbox checked={visibleColumns[k] !== false} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.2 } }} />
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownloadCSV} size="small" aria-label="Download CSV" sx={{ padding: '6px' }}>
                <SaveAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showSummary ? (
          <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {summaryError ? <Box sx={{ p: 1.5, color: 'error.main', fontSize: '0.875rem' }}>{summaryError}</Box> : null}
            <HoverExpandDataGridPro
              rows={summaryGridRows}
              columns={summaryGridColumns}
              loading={isLoadingSummary}
              disableVirtualization
              disableRowSelectionOnClick
              hideFooterSelectedRowCount
              pagination
              paginationMode="client"
              pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
              initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
              rowHeight={denseMode ? 36 : 52}
              columnHeaderHeight={denseMode ? 40 : 56}
              sx={{ flex: 1, minHeight: 0 }}
            />
          </Paper>
        ) : (
        <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {detailsError ? <Box sx={{ p: 1.5, color: 'error.main', fontSize: '0.875rem' }}>{String(detailsError)}</Box> : null}
          <HoverExpandDataGridPro
            rows={detailGridRows}
            columns={detailGridColumns}
            loading={isLoadingDetails}
            disableVirtualization
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
            initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
            rowHeight={denseMode ? 36 : 52}
            columnHeaderHeight={denseMode ? 40 : 56}
            sx={{ flex: 1, minHeight: 0 }}
          />
          </Paper>
        )}
      </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DashboardLayout>
  );
};

export default FedexShippingAnalysis;
